import { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, ExternalLink, Map } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Section } from '@/components/ui/Section';
import { ParticleBackground } from '@/components/ui/ParticleBackground';
import { ServerStatus } from '@/components/ServerStatus';
import { RealtimeMapModal } from '@/components/RealtimeMapModal';
import { siteConfig } from '@/config/site';

export function HeroSection() {
  const [copied, setCopied] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const copyIP = async () => {
    try {
      await navigator.clipboard.writeText(siteConfig.serverIP);
      setCopied(true);
      toast.success('IP Copied!', {
        description: siteConfig.serverIP,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy IP');
    }
  };

  return (
    <Section
      fullWidth
      animate={false}
      className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden"
    >
      {/* Background */}
      <ParticleBackground particleCount={25} />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <img
            src={siteConfig.logo}
            alt={siteConfig.name}
            className="w-32 h-32 md:w-40 md:h-40 mx-auto object-contain animate-float"
          />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4"
        >
          <span className="text-gradient">{siteConfig.name}</span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg md:text-xl text-muted-foreground mb-8"
        >
          {siteConfig.tagline}
        </motion.p>

        {/* Server Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-md mx-auto mb-8"
        >
          <ServerStatus />
        </motion.div>

        {/* Server IP */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted border border-border">
            <code className="text-sm md:text-base font-mono text-foreground">
              {siteConfig.serverIP}
            </code>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg hover:shadow-glow transition-shadow"
          >
            <a
              href={siteConfig.links.discord}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              Join Discord
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>

          <Button
            onClick={copyIP}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto font-semibold"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy IP
              </>
            )}
          </Button>

          <Button
            onClick={() => setMapOpen(true)}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto font-semibold"
          >
            <Map className="h-4 w-4" />
            Realtime Map
          </Button>
        </motion.div>
      </div>

      {/* Realtime Map Modal */}
      <RealtimeMapModal isOpen={mapOpen} onClose={() => setMapOpen(false)} />
    </Section>
  );
}
